import { TFunction, useAuthenticationContext } from '@psyomics/ui-common';
import { ColumnDef, Button, useFormatCellAsDate, CellDateFormatter } from '@psyomics/ui-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ReferralSessionResult } from './types';

function nameColumn(t: TFunction): ColumnDef<ReferralSessionResult> {
  return {
    accessorFn: (row) => `${row.referral.patient.givenName} ${row.referral.patient.familyName}`,
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('model.referrals.name'),
    id: 'name',
  };
}

function eprIdColumn(t: TFunction): ColumnDef<ReferralSessionResult> {
  return {
    accessorFn: (row) => row.referral.patient.piSystemId.id,
    cell: (info) => info.getValue(),
    enableSorting: true,
    header: t('model.referrals.patient-pi-system-id'),
    id: 'piSystemId',
  };
}

function serviceColumn(t: TFunction): ColumnDef<ReferralSessionResult> {
  return {
    accessorFn: (row) => row.referral.service.code,
    enableSorting: true,
    header: t('model.referrals.service'),
    cell: (info) => info.getValue(),
  };
}

function createdAtColumn(
  dateFormatter: CellDateFormatter<ReferralSessionResult>,
  t: TFunction
): ColumnDef<ReferralSessionResult> {
  return {
    accessorKey: 'createdAt',
    cell: dateFormatter,
    enableSorting: true,
    header: t('model.referrals.referral-date'),
    id: 'referredAt',
  };
}

function completedAtColumn(
  dateFormatter: CellDateFormatter<ReferralSessionResult>,
  t: TFunction
): ColumnDef<ReferralSessionResult> {
  return {
    accessorKey: 'completedAt',
    cell: dateFormatter,
    enableSorting: true,
    header: t('model.referrals.completion-date'),
    id: 'completedAt',
  };
}

function statusColumn(t: TFunction): ColumnDef<ReferralSessionResult> {
  return {
    accessorKey: 'status',
    enableSorting: true,
    header: t('model.common.status'),
    cell: (info) => t(`model.referrals.status.${info.getValue()}`),
  };
}

function actionsColumn(t: TFunction, organisation: string): ColumnDef<ReferralSessionResult> {
  return {
    id: 'actions',
    header: t('page.referrals.report'),
    cell: (info) => {
      if (!info.row.original.reportAvailable) return null;

      return (
        <Link
          rel="noopener noreferrer"
          target="_blank"
          to={`/account/${organisation}/home/report/${info.row.original.referral.id}/${info.row.original.id}`}
        >
          <Button appearance="secondary" size="sm">
            {t('page.referrals.view-report')}
          </Button>
        </Link>
      );
    },
  };
}

export const useTableColumns = (): ColumnDef<ReferralSessionResult>[] => {
  const { t } = useTranslation();
  const { organisation } = useAuthenticationContext();
  const dateFormatter = useFormatCellAsDate<ReferralSessionResult>(t, 'datetime.short');

  return [
    nameColumn(t),
    eprIdColumn(t),
    serviceColumn(t),
    createdAtColumn(dateFormatter, t),
    completedAtColumn(dateFormatter, t),
    statusColumn(t),
    actionsColumn(t, organisation),
  ];
};
